import * as angular from "angular";
import {IController} from "angular";
import * as _ from "lodash";
import {firstValueFrom} from "rxjs";
import {VisitPlanHttpService} from "../../angular/services/visit-plan-http.service";
import {SchedulingProjectStatusEnum} from "../../enums/scheduling-project-status.enum";
import {mapVisitScheduleOverviewToReport} from "../../models/visit-schedule-overview.functions";
import {VisitScheduleOverviewReportModel} from "../../models/visit-schedule-overview.model";
import {SchedulingProjectFilterCascadeService} from "../../services/scheduling-project-filter-cascade.service";
import {VisitScheduleDaysStatusEnum} from "../../enums/visit-schedule-days-status.enum";
import {VisitScheduleOverviewTotalsModel} from "../../models/visit-schedule-overview-totals.model";

const TOTAL_TAB_KEY: keyof VisitScheduleOverviewTotalsModel = 'total';

const REPORTS_TOTALS_TABS: (keyof VisitScheduleOverviewTotalsModel)[] = [
  TOTAL_TAB_KEY,
  'dispatched',
  'partialDispatched',
  'notDispatched',
  'published',
  'partialPublished',
  'notPublished'
];

export class SchedulingProjectReportsViewComponent implements IController {

  from: number;
  branchIds: string[];
  groupIds: string[];
  tags: string[];
  serviceAreaIds: string[];
  statuses: VisitScheduleDaysStatusEnum[];
  schedulingProjectStatuses: SchedulingProjectStatusEnum[];

  public isArchive: boolean = false;
  public filtersConfig: any;
  public filteredBranchIds: string[];
  public filteredGroupIds: string[];
  public reports: VisitScheduleOverviewReportModel[] = [];
  public visitPlanStatusOptions: string[];
  public templateOptions: any[] = [];
  public templateOptionIds: string[] = [];
  public templateOptionsWrapper: any;
  public selectedTemplateIds: string[] = [];
  public paginationData: any;
  public page: number = 0;
  public pageSize: number = 20;
  public isLoading: boolean = false;
  public totalsTabs: (keyof VisitScheduleOverviewTotalsModel)[] = REPORTS_TOTALS_TABS;
  public totals: Partial<VisitScheduleOverviewTotalsModel> = {};
  public totalsLoading: boolean = false;
  public selectedTotalsTab: string;

  /* @ngInject */
  constructor(private visitPlanHttpService: VisitPlanHttpService,
              private schedulingProjectFilterCascadeService: SchedulingProjectFilterCascadeService,
              private moment, private $timeout,
              private $log, private $filter) {
  }

  $onInit() {
    this.branchIds = this.branchIds || [];
    this.groupIds = this.groupIds || [];
    this.tags = this.tags || [];
    this.serviceAreaIds = this.serviceAreaIds || [];
    this.statuses = this.statuses || [];
    this.schedulingProjectStatuses = this.schedulingProjectStatuses || [];
    this.visitPlanStatusOptions = Object.keys(VisitScheduleDaysStatusEnum);
    this._syncSelectedTotalsTab();

    this.filtersConfig = {
      date: this.from || this.moment().valueOf(),
      queryString: undefined
    };

    this._loadTemplateOptions();
    if (this.tags?.length) {
      this._applyTagsFilterChange(true);
    } else {
      this.getReports();
    }
  }

  onQueryChanged(query) {
    this.filtersConfig.queryString = query;
    this.page = 0;
    this.getReports();
  }

  onDatesBrowse(isNext) {
    const date = isNext ?
      this.moment(this.filtersConfig.date).add(1, 'day').valueOf() :
      this.moment(this.filtersConfig.date).subtract(1, 'day').valueOf();
    this.onDateChange(date);
  }

  onDateChange(date) {
    this.filtersConfig.date = date;
    this.page = 0;
    this.getReports();
  }

  onBranchesChange(branchIds: string[]) {
    if (branchIds === this.branchIds) return;
    this.branchIds = branchIds;
    this.page = 0;
    this.getReports();
  }

  onTagsSelect(tags: string[]) {
    if (_.isEqual(tags, this.tags)) return;
    this.tags = tags || [];
    this.page = 0;
    this._applyTagsFilterChange(true);
  }

  onGroupsSelect(groupIds: string[]) {
    if (_.isEqual(groupIds, this.groupIds)) return;
    this.groupIds = groupIds;

    if (groupIds?.length) {
      this._applyBranchesFromGroups(groupIds, true);
    } else {
      this.filteredBranchIds = undefined;
      this.branchIds = [];
      this.page = 0;
      this.getReports();
    }
  }

  onChangeVisitPlanStatuses(statuses: VisitScheduleDaysStatusEnum[]) {
    this.statuses = statuses || [];
    this._syncSelectedTotalsTab();
    this.page = 0;
    this.getReports();
  }

  onTotalsTabSelect(tabKey: keyof VisitScheduleOverviewTotalsModel) {
    this.statuses = tabKey === TOTAL_TAB_KEY ? [] : [_.snakeCase(tabKey).toUpperCase() as VisitScheduleDaysStatusEnum];
    this.selectedTotalsTab = tabKey;
    this.page = 0;
    this.getReports();
  }

  onChangeIsArchive(isArchive: boolean) {
    this.isArchive = isArchive;
    this.page = 0;
    this.getReports();
  }

  onTemplateChange(selectedIds: string[]) {
    this.selectedTemplateIds = selectedIds || [];
    this.page = 0;
    this.getReports();
  }

  onPaginationChange(pageIndex) {
    this.page = pageIndex - 1;
    this.getReports(false);
  }

  private _applyTagsFilterChange(reload: boolean) {
    this.schedulingProjectFilterCascadeService.applyTagsFilter(this.tags)
      .then((state) => {
        this.filteredGroupIds = state.filteredGroupIds;
        this.groupIds = state.groupIds;
        this.filteredBranchIds = state.filteredBranchIds;
        this.branchIds = state.branchIds;
        if (reload) {
          this.getReports();
        }
      })
      .catch((error) => {
        if (error?.stale) {
          return;
        }
        this.filteredGroupIds = undefined;
        this.groupIds = [];
        this.filteredBranchIds = undefined;
        this.branchIds = [];
        if (reload) {
          this.getReports();
        }
      })
      .finally(() => this.$timeout(() => {}, 0));
  }

  private _applyBranchesFromGroups(groupIds: string[], reload: boolean) {
    this.schedulingProjectFilterCascadeService.applyBranchesFromGroups(groupIds)
      .then((state) => {
        this.filteredBranchIds = state.filteredBranchIds;
        this.branchIds = state.branchIds;
        if (reload) {
          this.getReports();
        }
      })
      .catch((error) => {
        if (error?.stale) {
          return;
        }
        this.filteredBranchIds = undefined;
        this.branchIds = [];
        if (reload) {
          this.getReports();
        }
      })
      .finally(() => this.$timeout(() => {}, 0));
  }

  private getReports(reloadTotals: boolean = true) {
    if (reloadTotals) {
      this._loadTotals();
    }
    this.isLoading = true;
    const params = this._getQueryParams();
    firstValueFrom(this.visitPlanHttpService.getVisitScheduleOverview(params))
      .then((response) => {
        this.paginationData = response;
        this.reports = (response.content || []).map((overview) => {
          const report = mapVisitScheduleOverviewToReport(overview, params.from, params.to);
          return {
            ...report,
            statusLabel: report.status ? this.$filter('translate')('VISIT_PLAN_STATUS.' + report.status) : undefined
          };
        });
      })
      .catch((error) => {
        this.$log.error('Failed to load scheduling project reports', error);
        this.reports = [];
        this.paginationData = undefined;
      })
      .finally(() => {
        this.isLoading = false;
        this.$timeout(() => {}, 0);
      });
  }

  private _loadTotals() {
    const params = _.omit(this._getQueryParams(), ['page', 'size', 'sort', 'dayTotalStatuses']);
    this.totalsLoading = true;
    firstValueFrom(this.visitPlanHttpService.getVisitScheduleOverviewTotals(params))
      .then((totals) => {
        this.totals = totals || {};
      })
      .catch((error) => {
        this.$log.error('Failed to load scheduling project reports totals', error);
        this.totals = {};
      })
      .finally(() => {
        this.totalsLoading = false;
        this.$timeout(() => {}, 0);
      });
  }

  private _syncSelectedTotalsTab() {
    if (_.isEmpty(this.statuses)) {
      this.selectedTotalsTab = TOTAL_TAB_KEY;
    } else if (this.statuses.length === 1 && _.includes(this.totalsTabs, _.camelCase(this.statuses[0]))) {
      this.selectedTotalsTab = _.camelCase(this.statuses[0]);
    } else {
      this.selectedTotalsTab = undefined;
    }
  }

  private _getQueryParams() {
    const params: any = {
      from: this.moment(this.filtersConfig.date).startOf('day').valueOf(),
      to: this.moment(this.filtersConfig.date).endOf('day').valueOf(),
      isArchive: this.isArchive,
      page: this.page,
      size: this.pageSize,
      sort: 'id,desc'
    };

    if (this.filtersConfig.queryString) {
      params.query = this.filtersConfig.queryString;
    }
    if (!_.isEmpty(this.branchIds)) {
      params.branchIds = this.branchIds;
    }
    if (!_.isEmpty(this.groupIds)) {
      params.groupIds = this.groupIds;
    }
    if (!_.isEmpty(this.tags)) {
      params.tag = this.tags;
    }
    if (!_.isEmpty(this.serviceAreaIds)) {
      params.areaIds = this.serviceAreaIds;
    }
    if (!_.isEmpty(this.selectedTemplateIds)) {
      params.templateIds = this.selectedTemplateIds;
    }
    if (!_.isEmpty(this.schedulingProjectStatuses)) {
      params.spStatus = this.schedulingProjectStatuses;
    }
    if (!_.isEmpty(this.statuses)) {
      params.dayTotalStatuses = this.statuses;
    }

    return params;
  }

  private _loadTemplateOptions() {
    this.visitPlanHttpService.getAllTemplates(200).subscribe({
      next: (response) => {
        this.templateOptions = (response.list || []).map((template) => {
          return {
            id: template.id,
            name: template.name
          };
        });
        this.templateOptionIds = _.map(this.templateOptions, 'id');
        this.templateOptionsWrapper = {
          list: this.templateOptions,
          propertyRef: 'name'
        };
        this.$timeout(() => {}, 0);
      },
      error: (error) => {
        this.$log.error('Failed to load scheduling project templates', error);
      }
    });
  }

}


angular
  .module('bringoz.admin.scheduling-project')
  .component('schedulingProjectReportsView', {
    bindings: {
      from: '<',
      branchIds: '<?',
      groupIds: '<?',
      tags: '<?',
      serviceAreaIds: '<?',
      statuses: '<?',
      schedulingProjectStatuses: '<?',
      isArchive: '<?'
    },
    template: require('./scheduling-project-reports-view.html').default,
    controller: SchedulingProjectReportsViewComponent
  });
